// FIN-999/FIN-1065: the rules for what a tax-account row in the accounts list says and offers,
// shared by apps/client's TaxAccountRow and src/'s TaxAccountSection so the two apps cannot drift.
// Pure, with its own subpath export, so src/ can take it without this package's React components
// (same as tax/grantStage).

import { resolveFormattingLocale, type Locale } from "@financialharmony/i18n"
import { taxGrantStage, type TaxGrantRecordedOutcome, type TaxGrantStage } from "./grantStage"

/**
 * Why a tax account row has no current figure — the API's `balanceUnavailableReason`, plus
 * `cancelled`: a `pending` account whose latest trip the user cancelled at the authority, derived
 * from the account's grant outcome.
 */
export type TaxAccountUnavailableReason =
  | "not_connected"
  | "cancelled"
  | "pending"
  | "insufficient_scope"
  | "not_readable"
  | "revoked"
  | "incomplete_data"
  | "closed"
  | "no_tax_account"

/** Which row actions an account offers. */
export interface TaxRowActions {
  /** "Finish": the setup is unfinished and the next step is a fresh trip to the authority (§3a). */
  finish: boolean
  /** "Discard": never finished and never read, as the server judged it (scenarios C, N). */
  discard: boolean
  /** "Reconnect": the authority withdrew our access (§5). */
  reconnect: boolean
  /**
   * "Check again": the authority shows no tax account for the company yet (FIN-1453). Offered only
   * where there is no figure, so it never implies a shown balance is stale.
   */
  checkAgain: boolean
}

/** Reasons where the next step is a fresh trip to the authority. */
const FINISHABLE: ReadonlySet<TaxAccountUnavailableReason> = new Set(["not_connected", "cancelled", "insufficient_scope"])

/** A `pending` row's reason by stage — the same rule the connect dialogs read (FIN-984/FIN-998). */
const PENDING_REASON: Partial<Record<TaxGrantStage, TaxAccountUnavailableReason>> = {
  signed: "pending",
  declined: "cancelled",
}

/**
 * The row's reason. A `pending` account is split three ways by how its latest trip ended, because
 * each needs a different sentence: signed (the authority is processing it), cancelled, or never
 * came back (unfinished — the same as a draft).
 */
export function taxRowReason(
  balanceUnavailableReason: string | null | undefined,
  grantOutcome: string | null | undefined,
): TaxAccountUnavailableReason | undefined {
  const reason = (balanceUnavailableReason ?? undefined) as TaxAccountUnavailableReason | undefined
  if (reason !== "pending") return reason
  const stage = taxGrantStage("pending", grantOutcome as TaxGrantRecordedOutcome | null | undefined)
  return PENDING_REASON[stage] ?? "not_connected"
}

/**
 * The actions a row offers, from its reason and the server's `discardable` hint.
 *
 * An activating row offers none: the owner has said they signed and a background check is
 * confirming it, so Finish would mint a second link and Discard would delete the account the
 * check is about to promote. The window is ten minutes; after it the row is unfinished again.
 */
export function taxRowActions(
  reason: TaxAccountUnavailableReason | undefined,
  discardable: boolean,
  activating = false,
): TaxRowActions {
  if (activating) return { finish: false, discard: false, reconnect: false, checkAgain: false }
  return {
    finish: reason !== undefined && FINISHABLE.has(reason),
    discard: discardable,
    reconnect: reason === "revoked",
    checkAgain: reason === "no_tax_account",
  }
}

/**
 * What an activating row says (FIN-1301): that the permission is being confirmed, or, when the
 * authority's published closure is what stopped the last check, when it reopens. Either way it is
 * still waiting; nothing here asks the owner to do anything.
 */
export function taxActivatingCopy(authorityName: string, closedUntilLabel?: string): string {
  return closedUntilLabel
    ? `${authorityName} is closed until ${closedUntilLabel} — we'll keep checking`
    : `Confirming your permission with ${authorityName}…`
}

/** The `Intl` tag for a UI language, so callers hand over the language and never a tag. */
const formattingTag = (locale: Locale): string => resolveFormattingLocale(locale).tag

/** The hour the authority reopens, in Swedish time: "06:10". */
export function formatTaxClosedUntil(iso: string, locale: Locale): string {
  return new Date(iso).toLocaleTimeString(formattingTag(locale), { hour: "2-digit", minute: "2-digit", timeZone: TIME_ZONE })
}

/**
 * Who reads a tax row: the company that owns the account (apps/client), or a consultant looking
 * at a client's account (src/). The company is addressed as "you"; a consultant never is, since
 * the debt and the permission are the client's, not theirs.
 */
export type TaxRowAudience = "owner" | "consultant"

/** The three sentences a not-yet-connected row can carry, named from the authority registry. */
export function taxUnfinishedRowCopy(
  authorityName: string,
  audience: TaxRowAudience,
): Record<"not_connected" | "cancelled" | "pending", string> {
  if (audience === "consultant") {
    return {
      not_connected: "Finish connecting to see the balance",
      cancelled: `Cancelled at ${authorityName} — finish connecting to see the balance`,
      pending: `${authorityName} is still processing the permission`,
    }
  }
  return {
    not_connected: "Finish connecting to see what you owe",
    cancelled: `Cancelled at ${authorityName} — finish connecting to see what you owe`,
    pending: `${authorityName} is still processing your permission`,
  }
}

/** What the discard confirmation says, in both apps. */
export function taxDiscardCopy(accountLabel: string) {
  return {
    title: `Discard this unfinished ${accountLabel} setup?`,
    description: "Nothing was connected, so nothing is lost. You can start again whenever you like.",
    keep: "Keep it",
    confirm: "Discard",
    pending: "Discarding…",
  } as const
}

/** Why a discard did not happen, in both apps. */
export const TAX_DISCARD_ERRORS = {
  /** The server refused: the account was connected meanwhile. An answer, not a fault. */
  refusedConnected: "This tax account has been connected, so it can't be discarded.",
  failed: "Could not discard this setup. Please try again.",
} as const

/** Our sync times are read in Swedish time, whatever the viewer's clock says (FIN-999). */
const TIME_ZONE = "Europe/Stockholm"
/** Only the calendar-day key below: a fixed ISO-shaped format, never shown to a user. */
const DAY_KEY_LOCALE = "sv-SE"

/** The authority's calendar date: "30 juni", with the year when it is not this year's. */
export function formatTaxAsOfDate(isoDate: string, now: Date, locale: Locale): string {
  // A date-only string is a calendar day, not an instant: format it in UTC, where it was parsed.
  const date = new Date(`${isoDate.slice(0, 10)}T00:00:00Z`)
  const sameYear = date.getUTCFullYear() === Number(stockholmDay(now).slice(0, 4))
  return date.toLocaleDateString(formattingTag(locale), {
    day: "numeric",
    month: "short",
    ...(sameYear ? {} : { year: "numeric" }),
    timeZone: "UTC",
  })
}

/** "06:14" when it was today in Stockholm, else "27 juni 06:14" — a stale time never reads as fresh. */
export function formatTaxSyncedAt(iso: string, now: Date, locale: Locale): string {
  const at = new Date(iso)
  const time = at.toLocaleTimeString(formattingTag(locale), { hour: "2-digit", minute: "2-digit", timeZone: TIME_ZONE })
  if (stockholmDay(at) === stockholmDay(now)) return time
  const day = at.toLocaleDateString(formattingTag(locale), { day: "numeric", month: "short", timeZone: TIME_ZONE })
  return `${day} ${time}`
}

/**
 * When the schedule asks the authority next, as a date: the Stockholm calendar day of the instant,
 * never a time of day, because the schedule promises a day and not an hour. Both apps show it.
 */
export function formatTaxNextCheck(iso: string, now: Date, locale: Locale): string {
  return formatTaxAsOfDate(stockholmDay(new Date(iso)), now, locale);
}

/** YYYY-MM-DD in Stockholm, assembled from the parts so it does not depend on any language's date order. */
function stockholmDay(at: Date): string {
  const parts = new Intl.DateTimeFormat(DAY_KEY_LOCALE, {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    timeZone: TIME_ZONE,
  }).formatToParts(at)
  const part = (type: "year" | "month" | "day") => parts.find((p) => p.type === type)?.value ?? ""
  return `${part("year")}-${part("month")}-${part("day")}`
}
